import React from "react";
import { ZButton, ZGrayRow, ZIcon, ZModal, ZTypography } from "@atoms";
import { IChangeSuggestionUpdatesModalProps } from "./interface";
import "./index.less";
import ZTable from "../../ZTable";
import {
	capitalizeFirstLetter,
	ChangeSuggestionAction,
	Client,
	ClientContact,
	MergeTagVariableDataType,
	splitAfterUnderscore,
	SuggestedChange,
	VendorClient,
	ZomentumEntities
} from "@/Contracts";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { customDateFormatter } from "@/Utils/common";
import useDatePattern from "@/Hooks/useDatePattern";
import { ColumnsType } from "antd/lib/table";

const ZChangeSuggestionUpdatesModal: React.FC<
	IChangeSuggestionUpdatesModalProps
> = ({
	visible,
	changeSuggestion,
	onCancel,
	onAccept,
	onReject,
	isLoading,
	clientCompanyName
}) => {
	const datePattern = useDatePattern();
	const getColumns = () => {
		const columns: ColumnsType<{
			fieldName: string;
			earlierValue: string;
			updatedValue: string;
		}> = [
			{
				dataIndex: "fieldName",
				key: "fieldName",
				title: "Field Updated"
			}
		];
		if (changeSuggestion?.action === ChangeSuggestionAction.MODIFICATION) {
			columns.push(
				{
					dataIndex: "earlierValue",
					key: "earlierValue",
					title: "Earlier Value"
				},
				{
					dataIndex: "arrowIcon",
					key: "arrowIcon",
					render: () => (
						<ZIcon
							name={EIcons.ARROWRIGHT}
							size="sm"
							color={EColors.GEEKBLUE}
							colorVariant={4}
						/>
					)
				},
				{
					dataIndex: "updatedValue",
					key: "updatedValue",
					title: "Updated Value"
				}
			);
		} else {
			columns.push({
				dataIndex: "updatedValue",
				key: "updatedValue",
				title: "Value"
			});
		}
		return columns;
	};
	const onAcceptChanges = () => {
		ZModal.confirm({
			title: "Are you sure you want to accept the changes?",
			content:
				"Accepting changes will update the clients details in all the opportunities across all the vendors that client is associated with.",
			okText: "Yes, Accept",
			cancelText: "Cancel",
			onOk: onAccept
		});
	};
	const onRejectChanges = () => {
		ZModal.confirm({
			title: "Are you sure you want to ignore the changes?",
			content:
				"Ingnoring these changes will remove this change request by the vendor from the client.",
			okText: "Ignore Changes",
			cancelText: "Cancel",
			onOk: onReject
		});
	};
	const getModalTitle = () => {
		if (
			changeSuggestion?.entity_type === ZomentumEntities.Client &&
			!!changeSuggestion?.entity
		) {
			const clientEntity = changeSuggestion.entity as Client;
			return `Client Details For ${clientEntity.name} Updated By Vendor`;
		} else if (
			changeSuggestion?.entity_type === ZomentumEntities.VendorClient &&
			!!changeSuggestion?.entity
		) {
			const clientEntity = changeSuggestion.entity as VendorClient;
			return `Client Details For ${clientEntity.name} Updated By Partner`;
		} else if (
			changeSuggestion?.entity_type === ZomentumEntities.ClientContact &&
			!!clientCompanyName
		) {
			return `Client Details For ${clientCompanyName} Updated By Vendor`;
		} else if (
			changeSuggestion?.entity_type ===
				ZomentumEntities.VendorClientContact &&
			!!clientCompanyName
		) {
			return `Client Details For ${clientCompanyName} Updated By Partner`;
		} else {
			return "";
		}
	};

	const getDataSource = () => {
		return (
			changeSuggestion?.suggested_changes.map(suggestedChange => {
				const existingValue: SuggestedChange | undefined =
					changeSuggestion.existing_values.find(
						existingValue =>
							existingValue.variable_name ===
							suggestedChange.variable_name
					);
				const getFieldName = () => {
					switch (suggestedChange.display_name) {
						case "name.first":
							return "First Name";
						case "name.last":
							return "Last Name";
						default:
							return capitalizeFirstLetter(
								splitAfterUnderscore(
									suggestedChange.display_name
										.split(".")
										.join(" ")
								)
							);
					}
				};
				return {
					fieldName: getFieldName(),
					earlierValue:
						existingValue?.data_type ===
							MergeTagVariableDataType.DATE &&
						!!existingValue.value
							? customDateFormatter(
									existingValue.value,
									datePattern
							  )
							: existingValue?.value ?? "",
					updatedValue:
						suggestedChange?.data_type ===
							MergeTagVariableDataType.DATE &&
						!!suggestedChange.value
							? customDateFormatter(
									suggestedChange.value,
									datePattern
							  )
							: suggestedChange?.value ?? ""
				};
			}) ?? []
		);
	};

	const ZClientContactDetailRow = () => {
		if (
			changeSuggestion?.entity_type === ZomentumEntities.ClientContact ||
			changeSuggestion?.entity_type ===
				ZomentumEntities.VendorClientContact
		) {
			if (changeSuggestion.action === ChangeSuggestionAction.CREATION) {
				const firstName = changeSuggestion.suggested_changes.find(
					change => change.variable_name === "name.first"
				)?.value;
				const lastName = changeSuggestion.suggested_changes.find(
					change => change.variable_name === "name.last"
				)?.value;
				return (
					<ZGrayRow className="p-4 border border-gray-4 border-solid">
						<ZTypography.ZText
							level={1}
							className="mr-6"
							weight={EFontWeights.MEDIUM}
						>
							Contact Added Under Client Name :
						</ZTypography.ZText>
						<ZTypography.ZText level={1}>
							{`${firstName} ${lastName}`}
						</ZTypography.ZText>
					</ZGrayRow>
				);
			} else if (
				changeSuggestion.action === ChangeSuggestionAction.MODIFICATION
			) {
				return (
					<ZGrayRow className="p-4 border border-gray-4 border-solid">
						<ZTypography.ZText
							weight={EFontWeights.MEDIUM}
							className="mr-10"
							colorVariant={8}
							level={1}
						>
							Changes Updated by Vendor for
						</ZTypography.ZText>
						<ZTypography.ZText level={1} className="mr-6">
							Contact Name:
						</ZTypography.ZText>
						<ZTypography.ZText level={1}>
							{
								(changeSuggestion?.entity as ClientContact)
									?.name.full
							}
						</ZTypography.ZText>
					</ZGrayRow>
				);
			} else {
				return null;
			}
		} else {
			return null;
		}
	};

	return (
		<ZModal
			visible={visible}
			size="xl"
			title={getModalTitle()}
			className="z-change-suggestion-updates-modal"
			onCancel={onCancel}
			footer={[
				<ZButton
					type="link"
					key="back-link"
					onClick={onCancel}
					iconName={EIcons.ARROWLEFT}
					loading={isLoading}
				>
					Back
				</ZButton>,
				<div key="empty-div" className="flex-1" />,
				<ZButton
					key="ignore-button"
					onClick={onRejectChanges}
					loading={isLoading}
				>
					Ignore Changes
				</ZButton>,
				<ZButton
					key="accept-changes-button"
					onClick={onAcceptChanges}
					type="primary"
					loading={isLoading}
				>
					Accept Changes
				</ZButton>
			]}
		>
			<ZClientContactDetailRow />
			<ZTable
				className="z-change-suggestion-updates-modal-table"
				dataSource={getDataSource()}
				columns={getColumns()}
				isTopBorder={false}
				pagination={false}
				loading={isLoading}
			/>
		</ZModal>
	);
};

export default ZChangeSuggestionUpdatesModal;
